<template>
  <div
    v-click-outside="emitClose"
    v-tooltip="tooltip"
    class="sf-dropdown2 tw-rounded-lg tw-content-center"
    :class="[
      dropdownClasses,
      { 'sf-dropdown2-with-actions tw-inline-flex': !!$slots.actions },
    ]"
    data-testid="dropdown"
    v-bind="$attrs"
    @click.stop.prevent="emitClicked"
  >
    <div :class="selectWrapperClassnames" data-testid="dropdown-wrapper">
      <div
        class="sf-dropdown2-select tw-rounded-lg"
        :class="selectClassNames"
        data-testid="dropdown-select"
        @click="emitTriggerClicked"
      >
        <slot name="trigger" />
      </div>
    </div>
    <slot name="actions" />
    <!-- mouseover, not mouseenter: the submenu handling is event delegation
      and needs an event per row crossing to switch panels and schedule
      closes. Re-hovers of the active trigger skip repositioning, so the
      re-runs stay cheap. -->
    <div
      :class="[
        dropdownMenuClassnames,
        { 'tw-flex tw-flex-col tw-top-8': !!$slots.actions && isOpen },
      ]"
      @click.capture="handleMenuClick"
      @mouseover="handleMenuHover"
      @mouseleave="scheduleSubmenuClose"
      @keydown="handleMenuKeydown"
    >
      <slot name="menu" />
    </div>
    <!-- Panels repeat the menu box bindings instead of sharing a wrapper
      div, so the DOM structure under existing consumers stays unchanged. -->
    <div
      v-for="id in submenuIds"
      v-show="activeSubmenu === id"
      :key="id"
      :data-submenu-panel="id"
      :class="dropdownMenuClassnames"
      @click.capture="handleMenuClick"
      @mouseover="handleMenuHover"
      @mouseleave="scheduleSubmenuClose"
      @keydown="handleMenuKeydown"
    >
      <slot :name="`submenu-${id}`" />
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  variants,
  drawerSizes,
  triggerSizes,
  drawerDirections,
} from './constants'
import { computed, provide, useSlots, watch } from 'vue'

import {
  ACTIVE_SUBMENU,
  useDropdownSubmenu,
} from '@app/shared/vue/composables/use-dropdown-submenu'

const slots = useSlots()

defineOptions({
  name: 'SfDropdownMenu',
})

interface DropdownProps {
  dropdownId: string
  isFullWidth?: boolean
  isDisabled?: boolean
  isOpen?: boolean
  noOptionsStyling?: boolean
  // Cap the menu at the trigger width so a long option can't widen the dropdown;
  // labels wrap (and long tokens break) within that width instead of pushing the
  // menu out. Off by default so existing dropdowns keep sizing to their content.
  constrainMenuToTriggerWidth?: boolean
  triggerSize?: string
  drawerSize?: string
  variant?: string
  alignOption?: string
  drawerDirection?: string
  isInsideOtherInput?: boolean
  unstyledTrigger?: boolean
  tooltip?: string
  selectClassNames?: string[]
  menuClassNames?: string[]
}

const props = withDefaults(defineProps<DropdownProps>(), {
  variant: variants.neutral,
  drawerSize: drawerSizes.default,
  triggerSize: triggerSizes.default,
  drawerDirection: drawerDirections.right,
  alignOption: 'l',
  tooltip: '',
  selectClassNames: () => [],
  menuClassNames: () => [],
})

const emit =
  defineEmits<{
    'dropdown-closed': [dropdownId: string]
    'dropdown-clicked': [dropdownId: string]
    'dropdown-trigger-clicked': [dropdownId: string]
  }>()

// Sub-menu panels are declared as #submenu-<id> slots; the wrapper divs
// carry a matching data-submenu-panel attribute so the shared composable can
// resolve and position them exactly like the custom element's slotted light
// DOM (which uses real slot attributes).
const {
  activeSubmenu,
  handleMenuClick,
  handleMenuHover,
  handleMenuKeydown,
  isSubmenuInteraction,
  resetSubmenu,
  scheduleSubmenuClose,
} = useDropdownSubmenu()

// Lets SfDropdownItem trigger rows bind aria-expanded to the open state.
provide(ACTIVE_SUBMENU, activeSubmenu)

// Submenu slots must be declared statically: slot presence is not tracked
// reactively, so a `v-if` on a whole <template #submenu-*> can leave this
// list stale.
const submenuIds = computed(() =>
  Object.keys(slots)
    .filter((name) => name.startsWith('submenu-'))
    .map((name) => name.slice('submenu-'.length))
)

watch(
  () => props.isOpen,
  (isOpen) => {
    if (!isOpen) {
      resetSubmenu()
    }
  }
)

const selectClassNames = computed(() => {
  return [
    {
      'sf-dropdown2-select-padding-small':
        props.triggerSize === triggerSizes.small && !props.unstyledTrigger,
      'tw-px-2.5 tw-py-1 tw-text-xs tw-leading-4':
        props.triggerSize === triggerSizes.extraSmall && !props.unstyledTrigger,
      'tw-p-0': props.unstyledTrigger,
      [`sf-dropdown2-select--${props.variant}`]: props.variant,
      'tw-py-1.5':
        !props.unstyledTrigger &&
        props.isInsideOtherInput &&
        props.triggerSize !== triggerSizes.extraSmall,
      'tw-py-[9px]':
        !props.unstyledTrigger &&
        !props.isInsideOtherInput &&
        props.triggerSize !== triggerSizes.extraSmall,
    },
    ...props.selectClassNames,
  ]
})

const selectWrapperClassnames = computed(() => {
  return {
    'sf-dropdown2-wrapper':
      !props.unstyledTrigger && props.triggerSize !== triggerSizes.extraSmall,
    'sf-dropdown2-wrapper--open': props.isOpen,
    [`sf-dropdown2-wrapper--${props.variant}`]: props.variant,
    'tw-w-full': props.isFullWidth,
  }
})

const dropdownClasses = computed(() => {
  return {
    'sf-disabled': props.isDisabled,
    [`sf-dropdown2-${props.variant}`]: props.variant,
    [`sf-align-${props.alignOption}`]: props.alignOption,
    'tw-w-full': props.isFullWidth,
  }
})

const dropdownMenuClassnames = computed(() => {
  return [
    {
      'sf-dropdown2-menu': !props.noOptionsStyling,
      'tw-mt-2': !props.noOptionsStyling,
      'sf-dropdown2-menu--open': props.isOpen,
      'sf-dropdown2-menu--large': props.drawerSize === drawerSizes.large,
      'sf-dropdown2-menu--constrained': props.constrainMenuToTriggerWidth,
      [`open-drawer-${props.drawerDirection}`]: props.drawerDirection,
    },
    ...props.menuClassNames,
  ]
})

const emitClose = () => {
  if (props.isOpen && !props.isDisabled) {
    emit('dropdown-closed', props.dropdownId)
  }
}

const emitClicked = (event: MouseEvent) => {
  // Sub-menu interactions (trigger rows, back elements, panel content) are
  // handled by the composable and must not toggle the dropdown itself.
  if (!props.isDisabled && !isSubmenuInteraction(event)) {
    emit('dropdown-clicked', props.dropdownId)
  }
}

const emitTriggerClicked = () => {
  if (!props.isDisabled) {
    emit('dropdown-trigger-clicked', props.dropdownId)
  }
}
</script>
<style lang="scss">
.sf-dropdown2-select-padding-small {
  padding: 2px 26px 2px 8px;
}

/* Invalid dropdown (select-class-names: ['sf-form-warning'], e.g. via a
   hasError prop). The trigger is a <div>, not a real .sf-form-select, so
   ui-kit's `.sf-form-warning.sf-form-select` rule never reaches it — apply the
   same warning palette to the trigger here (kept on :hover so it doesn't revert
   to the neutral hover border). */
.sf-dropdown2-select.sf-form-warning,
.sf-dropdown2-select.sf-form-warning:hover {
  border-color: theme('colors.sf-warning.light-1');
  background-color: theme('colors.sf-warning.subtle-2');
  color: theme('colors.sf-warning.DEFAULT');
}

.sf-dropdown2-menu--constrained {
  max-width: 100%;
}

/* Wrap (and break long tokens) so the row fits the capped width and the
   selected-item checkmark isn't pushed out of view. The min-width reset drops
   the ui-kit's `.sf-dropdown2-item { min-width: 160px }` floor, which otherwise
   keeps the item wider than a narrow trigger and clips the checkmark. */
.sf-dropdown2-menu--constrained .sf-dropdown2-item {
  min-width: 0;
  overflow-wrap: anywhere;
  white-space: normal;
}

.open-drawer-left {
  right: 0;
}

.open-drawer-right {
  left: 0;
}

.sf-dropdown2-with-actions {
  .sf-dropdown2-select {
    border-radius: 0.5rem 0 0 0.5rem;
  }

  .sf-dropdown2-trash {
    box-shadow: 0 1px 0 0 #e6e7e8;
  }

  .sf-dropdown2-dismiss {
    border-radius: 0 0.5rem 0.5rem 0;
    box-shadow: 0 1px 0 0 #e6e7e8;
  }

  .sf-dropdown2-menu {
    min-width: initial;
  }

  .sf-dropdown2-item {
    align-items: center;
  }
}
</style>
